// src/NavPayloadAction.ts
var NavPayloadAction = /* @__PURE__ */ ((NavPayloadAction2) => {
  NavPayloadAction2["ViewVdp"] = "view-vdp";
  NavPayloadAction2["ViewSrp"] = "view-srp";
  NavPayloadAction2["ViewTrades"] = "view-trade-offer";
  NavPayloadAction2["ViewFinancing"] = "view-terms";
  NavPayloadAction2["ViewMyCars"] = "view-my-cars";
  return NavPayloadAction2;
})(NavPayloadAction || {});
var NavPayloadAction_default = NavPayloadAction;

// src/DispatcherEvent.ts
var DispatcherEvent = /* @__PURE__ */ ((DispatcherEvent2) => {
  DispatcherEvent2["AddFavorite"] = "add_favorite";
  DispatcherEvent2["AddSavedSearch"] = "add_saved_search";
  DispatcherEvent2["ContinuePurchase"] = "continue_purchase";
  DispatcherEvent2["EnterFullscreen"] = "enter_fullscreen";
  DispatcherEvent2["FavoriteAdded"] = "favorite_added";
  DispatcherEvent2["FavoriteRemoved"] = "favorite_removed";
  DispatcherEvent2["LeaveFullscreen"] = "leave_fullscreen";
  DispatcherEvent2["Link"] = "link";
  DispatcherEvent2["LogIn"] = "log_in";
  DispatcherEvent2["OpenModal"] = "open_modal";
  DispatcherEvent2["PageReady"] = "page_ready";
  DispatcherEvent2["Share"] = "share";
  DispatcherEvent2["StartPreOrder"] = "start_preorder";
  DispatcherEvent2["StartPurchase"] = "start_purchase";
  DispatcherEvent2["ToggleTradeIn"] = "toggle_trade_in";
  return DispatcherEvent2;
})(DispatcherEvent || {});
var DispatcherEvent_default = DispatcherEvent;

// src/generateNativeMessageSender.ts
function toError(err) {
  return err instanceof Error ? err : new Error(String(err));
}
function generateNativeMessageSender({
  logError,
  source,
  verboseLogs
}) {
  return function sendMessageToNative(event, payload, { callback, onError } = {}) {
    const message = {
      event,
      payload,
      source
    };
    if (verboseLogs) {
      console.debug("Send message to native", message);
    }
    try {
      if (typeof window.nativeWebViewMessageHandler?.postMessage === "function") {
        window.nativeWebViewMessageHandler.postMessage(JSON.stringify(message));
        if (callback)
          callback();
      }
      if (typeof window.webkit?.messageHandlers?.nativeWebViewMessageHandler?.postMessage === "function") {
        window.webkit.messageHandlers.nativeWebViewMessageHandler.postMessage(
          JSON.stringify(message)
        );
        if (callback)
          callback();
      }
    } catch (err) {
      const error = toError(err);
      logError(error, { location: "Native message dispatch" });
      if (onError)
        onError(error);
    }
  };
}

// src/IncomingMessage.ts
var IncomingMessage = class {
  constructor(message, handlers) {
    this.handlers = handlers;
    this._wasHandled = false;
    this.event = message.event;
    this.payload = message.payload;
    this.source = message.source;
  }
  get wasHandled() {
    return this._wasHandled;
  }
  handleMessage() {
    this.handlers.forEach((handler) => {
      if (!handler.test(this))
        return;
      handler.handle(this);
      this._wasHandled = true;
    });
  }
};

// src/ListenerEvent.ts
var ListenerEvent = /* @__PURE__ */ ((ListenerEvent2) => {
  ListenerEvent2["AddFavorite"] = "add_favorite";
  ListenerEvent2["CloseModal"] = "close_modal";
  ListenerEvent2["Error"] = "error";
  ListenerEvent2["FavoriteAdded"] = "favorite_added";
  ListenerEvent2["FavoriteRemoved"] = "favorite_removed";
  ListenerEvent2["RemoveFavorite"] = "remove_favorite";
  ListenerEvent2["SaveSearch"] = "save_search";
  ListenerEvent2["TradeInToggled"] = "trade_in_toggled";
  ListenerEvent2["UpdateGeolocation"] = "update_geolocation";
  return ListenerEvent2;
})(ListenerEvent || {});
var ListenerEvent_default = ListenerEvent;

// src/MessageHandler.ts
var MessageHandler = class {
  constructor(test, handle) {
    this.test = test;
    this.handle = handle;
  }
};

// src/useAnchorClickMessageDispatcher.ts
import { useEffect, useMemo, useState } from "react";

// src/utils/safeParse.ts
function safeParse(input) {
  try {
    return JSON.parse(input);
  } catch (ex) {
    return void 0;
  }
}

// src/useAnchorClickMessageDispatcher.ts
function processUrl(url) {
  const urlPayload = {};
  const urlWithCvnaId = url.replace(
    /([?&]cvnaid=)([^&]+)(.*)/g,
    (_, cvnaIdQuery, cvnaIdRaw, otherQuery) => {
      const cvnaid = decodeURIComponent(cvnaIdRaw);
      urlPayload.cvnaid = cvnaid;
      return cvnaIdQuery + cvnaid + otherQuery;
    }
  );
  return {
    url: urlWithCvnaId,
    urlPayload
  };
}
function useAnchorClickMessageDispatcher({
  logError,
  source,
  verboseLogs
}) {
  const sendMessageToNative = useMemo(
    () => generateNativeMessageSender({ logError, source, verboseLogs }),
    [logError, source, verboseLogs]
  );
  const [previousEvent, setPreviousEvent] = useState({
    url: "",
    time: {}
  });
  useEffect(() => {
    const isNative = window.navigator.userAgent.includes(
      "CarvanaNativeWebView"
    );
    if (!isNative)
      return () => {
      };
    function onClick(ev) {
      if (!(ev.target instanceof Element))
        return;
      const target = ev.target.closest("a[href]");
      if (target == null || target.attributes.getNamedItem("href")?.value?.startsWith("#")) {
        return;
      }
      ev.preventDefault();
      const { url, urlPayload } = processUrl(target.href);
      const {
        nativeLink = "browser",
        nativePayload,
        nativeIncluded = "true"
      } = target.dataset || {};
      if (!safeParse(nativeIncluded))
        return;
      if (url === previousEvent.url && Date.now() - previousEvent.time.getTime() < 500) {
        setPreviousEvent({
          url,
          time: /* @__PURE__ */ new Date()
        });
        return;
      }
      setPreviousEvent({
        url,
        time: /* @__PURE__ */ new Date()
      });
      sendMessageToNative(DispatcherEvent_default.LeaveFullscreen, {});
      sendMessageToNative(DispatcherEvent_default.Link, {
        url,
        linkType: nativeLink,
        ...urlPayload,
        ...nativePayload ? safeParse(nativePayload) : void 0
      });
    }
    document.addEventListener("click", onClick, false);
    return () => {
      document.removeEventListener("click", onClick);
    };
  }, [previousEvent, sendMessageToNative]);
}

// src/useNativeMessageListener.ts
import { useEffect as useEffect2 } from "react";

// src/utils/toError.ts
function toError2(err) {
  return err instanceof Error ? err : new Error(String(err));
}

// src/useNativeMessageListener.ts
function maybeStringToObject(data) {
  return typeof data === "string" ? safeParse(data) : data;
}
function hasSourceProperty(value) {
  return typeof value === "object" && value != null && "source" in value;
}
function isNativeMessage(value) {
  return hasSourceProperty(value) && (value.source === "native_android" || value.source === "native_ios");
}
function isKnownNativeMessage(knownEvents, message) {
  return Object.values(knownEvents).includes(message.event);
}
function useNativeMessageListener({
  handlers,
  knownEvents,
  logErrorToTelemetry,
  logErrorToUser,
  verboseLogs
}) {
  useEffect2(() => {
    const handlePostMessage = (ev) => {
      const message = maybeStringToObject(ev.data);
      if (!isNativeMessage(message))
        return;
      if (verboseLogs) {
        console.debug("Message arrived", message);
      }
      if (!isKnownNativeMessage(knownEvents, message)) {
        logErrorToTelemetry(new Error("Unknown native event"), message);
        return;
      }
      const incomingMessage = new IncomingMessage(
        message,
        handlers
      );
      try {
        incomingMessage.handleMessage();
      } catch (err) {
        logErrorToTelemetry(toError2(err), {
          location: "Native message handler"
        });
        if (logErrorToUser)
          logErrorToUser(toError2(err));
      }
      if (!incomingMessage.wasHandled) {
        logErrorToTelemetry(new Error("Unhandled native event"), message);
      }
    };
    window.addEventListener("message", handlePostMessage, false);
    return () => {
      window.removeEventListener("message", handlePostMessage, false);
    };
  }, [handlers, knownEvents, logErrorToTelemetry, logErrorToUser, verboseLogs]);
}
export {
  DispatcherEvent_default as DispatcherEvent,
  IncomingMessage,
  ListenerEvent_default as ListenerEvent,
  MessageHandler,
  NavPayloadAction_default as NavPayloadAction,
  generateNativeMessageSender,
  useAnchorClickMessageDispatcher,
  useNativeMessageListener
};
//# sourceMappingURL=index.js.map
